<script lang="ts" setup>
import { ref, computed } from 'vue';
import { isLoaderLoginFormActivated } from '../../refs/login.form.refs';
import {
  isCategoryActivated,
  defaultProductLang,
  form_CatDesignation,
} from '@/refs/categories.refs';
import BaseButtonPrimary from '../common/BaseButtonPrimary.vue';
import { idToDelete } from '@/refs/commerce.refs';
import { isGlobalError, restaurantId } from '@/refs/common.refs';
import { CategorieMutation } from '@/mutations/categorie.mutations';
import { useDialogStore } from '@/store/dialog.store';
import axiosInstance from '@/services/interceptor.services';

const { handleCreateCategory, handleUpdateCategoryById } = CategorieMutation();
const dialogStore = useDialogStore();

const emit = defineEmits<{ (e: 'HandleClose'): void }>();

// ── Language selector (writable computed to avoid ES-import reassign issue) ────
const inputLang = computed({
  get: () => defaultProductLang.value,
  set: (val: 'fr' | 'nl' | 'en') => {
    defaultProductLang.value = val;
  },
});

// ── Translation API ─────────────────────────────────────────────────────────
async function translateAll(
  text: string,
  onResult: (code: string, translation: string) => void,
) {
  if (!text.trim()) return;
  try {
    const response = await axiosInstance.post(
      'translation',
      { text, sourceLanguage: defaultProductLang.value },
      { headers: { 'Accept-Language': defaultProductLang.value } },
    );
    const results: Array<{ code: string; translatedText: string }> =
      response.data?.data ?? [];
    for (const r of results) {
      if (r.code !== defaultProductLang.value)
        onResult(r.code, r.translatedText);
    }
  } catch {
    // silently fail
  }
}

const isAutoTranslating = ref(false);
let translateTimer: ReturnType<typeof setTimeout> | null = null;

const scheduleTranslation = (text: string) => {
  if (translateTimer) clearTimeout(translateTimer);
  if (!text.trim()) return;
  translateTimer = setTimeout(async () => {
    isAutoTranslating.value = true;
    await translateAll(text, (code, t) => {
      if (code === 'fr') form_CatDesignation.fr = t;
      if (code === 'nl') form_CatDesignation.nl = t;
      if (code === 'en') form_CatDesignation.en = t;
    });
    isAutoTranslating.value = false;
  }, 700);
};

const onInput = (lang: 'fr' | 'nl' | 'en', value: string) => {
  if (lang === defaultProductLang.value) scheduleTranslation(value);
};

// ── Submit ──────────────────────────────────────────────────────────────────
const handleSubmit = async () => {
  isLoaderLoginFormActivated.value = true;
  const payload = {
    designation: {
      fr: form_CatDesignation.fr,
      nl: form_CatDesignation.nl || form_CatDesignation.fr,
      en: form_CatDesignation.en || form_CatDesignation.fr,
    },
    description: '',
    restaurantId: restaurantId?.value,
  };

  if (isCategoryActivated.value) {
    try {
      await handleUpdateCategoryById(payload);
    } finally {
      setTimeout(() => {
        isLoaderLoginFormActivated.value = false;
        dialogStore.visible = false;
      }, 1000);
    }
  } else {
    try {
      await handleCreateCategory(payload);
    } catch {
      isGlobalError.value = false;
    } finally {
      setTimeout(() => {
        isLoaderLoginFormActivated.value = false;
        dialogStore.visible = false;
      }, 1000);
    }
  }
};

const handleClose = () => {
  idToDelete.value = null;
  emit('HandleClose');
};
</script>

<template>
  <form
    autocomplete="on"
    accept-charset="UTF-8"
    @submit.prevent="handleSubmit"
    class="space-y-6"
  >
    <!-- Header: label + language selector -->
    <div class="flex items-center justify-between">
      <p class="text-14px font-tt-normal-Twice text-grand-text">
        Nom de la catégorie <span class="text-red-600">*</span>
      </p>
      <div class="flex items-center gap-2">
        <span class="text-12px font-tt-normal-Normal text-[#74706B]"
          >Langue de saisie :</span
        >
        <select
          v-model="inputLang"
          class="text-12px font-tt-medium-Normal text-grand-text cursor-pointer rounded-lg border border-[#EEEDEC] bg-white px-2 py-1 outline-none"
        >
          <option value="fr">Français (FR)</option>
          <option value="nl">Nederlands (NL)</option>
          <option value="en">English (EN)</option>
        </select>
      </div>
    </div>

    <!-- FR row -->
    <div class="flex items-center gap-3">
      <span
        class="text-14px font-tt-medium-Normal w-8 shrink-0"
        :class="
          defaultProductLang === 'fr' ? 'text-[#F89303]' : 'text-grand-text'
        "
        >FR</span
      >
      <div
        v-if="defaultProductLang !== 'fr' && isAutoTranslating"
        class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[#EEEDEC] border-t-[#F89303]"
      />
      <div
        class="flex h-11 flex-1 items-center rounded-xl border border-solid bg-white px-3"
        :style="{
          borderColor: defaultProductLang === 'fr' ? '#F89303' : '#eeedec',
        }"
      >
        <input
          v-model="form_CatDesignation.fr"
          @input="onInput('fr', form_CatDesignation.fr)"
          type="text"
          placeholder="Ex: Boissons"
          class="text-grand-text text-14px font-tt-medium-Normal flex-1 bg-white focus:outline-none"
        />
      </div>
    </div>

    <!-- NL row -->
    <div class="flex items-center gap-3">
      <span
        class="text-14px font-tt-medium-Normal w-8 shrink-0"
        :class="
          defaultProductLang === 'nl' ? 'text-[#F89303]' : 'text-grand-text'
        "
        >NL</span
      >
      <div
        v-if="defaultProductLang !== 'nl' && isAutoTranslating"
        class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[#EEEDEC] border-t-[#F89303]"
      />
      <div
        class="flex h-11 flex-1 items-center rounded-xl border border-solid bg-white px-3"
        :style="{
          borderColor: defaultProductLang === 'nl' ? '#F89303' : '#eeedec',
        }"
      >
        <input
          v-model="form_CatDesignation.nl"
          @input="onInput('nl', form_CatDesignation.nl)"
          type="text"
          placeholder="Ex: Dranken"
          class="text-grand-text text-14px font-tt-medium-Normal flex-1 bg-white focus:outline-none"
        />
      </div>
    </div>

    <!-- EN row -->
    <div class="flex items-center gap-3">
      <span
        class="text-14px font-tt-medium-Normal w-8 shrink-0"
        :class="
          defaultProductLang === 'en' ? 'text-[#F89303]' : 'text-grand-text'
        "
        >EN</span
      >
      <div
        v-if="defaultProductLang !== 'en' && isAutoTranslating"
        class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[#EEEDEC] border-t-[#F89303]"
      />
      <div
        class="flex h-11 flex-1 items-center rounded-xl border border-solid bg-white px-3"
        :style="{
          borderColor: defaultProductLang === 'en' ? '#F89303' : '#eeedec',
        }"
      >
        <input
          v-model="form_CatDesignation.en"
          @input="onInput('en', form_CatDesignation.en)"
          type="text"
          placeholder="Ex: Beverages"
          class="text-grand-text text-14px font-tt-medium-Normal flex-1 bg-white focus:outline-none"
        />
      </div>
    </div>

    <div class="invisible h-[50px]" />

    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full">
          <BaseButtonPrimary
            type="submit"
            :isClicked="isLoaderLoginFormActivated"
            :label="isCategoryActivated ? 'Modifier' : 'Enregistrer'"
            style_of_btn="primary_color_of_btn"
            class="w-full text-white"
          />
        </div>
        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            label="Annuler"
            style_of_btn="secondary_color_of_btn"
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </form>
</template>
